import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  Image as ImageIcon,
} from "lucide-react";
import TintedImage from "./TintedImage";
import { getImageUrl } from "./configuratorConstants";

const ConfiguratorPreview = ({ name, steps, settings, onClose }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const pendingImageRef = useRef(null);

  const [selections, setSelections] = useState({});

  // Build pages: group consecutive steps with the same group
  const pages = useMemo(() => {
    const result = [];
    for (const step of steps) {
      // Skip hidden layer steps — they contribute images but aren't shown in the panel
      if (step.hiddenLayer) continue;
      const groupName = step.group?.trim() || null;
      if (groupName) {
        const existing = result.find((p) => p.group === groupName);
        if (existing) {
          existing.steps.push(step);
        } else {
          result.push({ group: groupName, label: groupName, steps: [step] });
        }
      } else {
        result.push({ group: null, label: step.name, steps: [step] });
      }
    }
    return result;
  }, [steps]);

  // Build a lookup of valid option IDs per step for stale-ID filtering
  const stepOptionIds = useMemo(() => {
    const map = {};
    steps.forEach((step) => {
      map[step.id] = new Set((step.options || []).map((o) => o.id));
    });
    return map;
  }, [steps]);

  // Helper: check if option is visible given a selections object
  const isOptionVisibleWith = useCallback(
    (option, sels) => {
      if (!option.dependencies || option.dependencies.length === 0) return true;
      return option.dependencies.every((dep) => {
        const selected = sels[dep.stepId];
        if (!selected) return false;
        // Filter out stale optionIds that no longer exist in the step
        const validIds = (dep.optionIds || []).filter((id) =>
          stepOptionIds[dep.stepId]?.has(id),
        );
        if (validIds.length === 0) return true;
        if (Array.isArray(selected))
          return selected.some((s) => validIds.includes(s.id));
        return validIds.includes(selected.id);
      });
    },
    [stepOptionIds],
  );

  const isOptionVisible = (option) => {
    return isOptionVisibleWith(option, selections);
  };

  // Cascade: after changing one step, fix all downstream dependent steps
  const cascadeSelections = useCallback(
    (sels) => {
      const next = { ...sels };

      // Determine which steps may auto-select (first page + hidden layers)
      const firstPageStepIds = new Set();
      let firstPageFound = false;
      for (const step of steps) {
        if (step.hiddenLayer) continue;
        const g = step.group?.trim() || null;
        if (!firstPageFound) {
          firstPageStepIds.add(step.id);
          if (!g) {
            firstPageFound = true;
            continue;
          }
        }
        if (firstPageFound && g) {
          const firstStep = steps.find((s) => firstPageStepIds.has(s.id));
          if (firstStep && (firstStep.group?.trim() || null) === g) {
            firstPageStepIds.add(step.id);
          }
        }
        if (!g && firstPageStepIds.size > 0 && firstPageFound) break;
      }

      // Multiple passes to handle chains (A → B → C)
      for (let pass = 0; pass < 5; pass++) {
        let settled = true;
        steps.forEach((step) => {
          const opts = step.options || [];
          const visibleOpts = opts.filter((o) => isOptionVisibleWith(o, next));
          const currentSel = next[step.id];
          const canAutoSelect =
            step.hiddenLayer || firstPageStepIds.has(step.id);

          if (step.type === "checkbox") {
            if (Array.isArray(currentSel)) {
              const filtered = currentSel.filter((sel) =>
                visibleOpts.some((v) => v.id === sel.id),
              );
              if (filtered.length !== currentSel.length) {
                next[step.id] = filtered;
                settled = false;
              }
            }
          } else if (step.type !== "frame-color") {
            if (!currentSel && visibleOpts.length > 0 && canAutoSelect) {
              const defaultOpt = step.defaultOptionId
                ? visibleOpts.find((o) => o.id === step.defaultOptionId)
                : null;
              const imgOpt = visibleOpts.find((o) => o.image);
              const pick = defaultOpt || imgOpt || visibleOpts[0] || null;
              if (pick) {
                next[step.id] = pick;
                settled = false;
              }
            } else if (
              currentSel &&
              !visibleOpts.some((v) => v.id === currentSel.id)
            ) {
              if (canAutoSelect) {
                const defaultOpt = step.defaultOptionId
                  ? visibleOpts.find((o) => o.id === step.defaultOptionId)
                  : null;
                const imgOpt = visibleOpts.find((o) => o.image);
                const pick = defaultOpt || imgOpt || visibleOpts[0] || null;
                if (pick) {
                  next[step.id] = pick;
                } else {
                  delete next[step.id];
                }
              } else {
                delete next[step.id];
              }
              settled = false;
            }
          }
        });
        if (settled) break;
      }
      return next;
    },
    [steps, isOptionVisibleWith],
  );

  const handleSelect = (stepId, option) => {
    const step = steps.find((s) => s.id === stepId);
    setSelections((prev) => {
      let next;
      if (step?.type === "checkbox") {
        const current = Array.isArray(prev[stepId]) ? prev[stepId] : [];
        const exists = current.find((o) => o.id === option.id);
        const updated = exists
          ? current.filter((o) => o.id !== option.id)
          : [...current, option];
        next = { ...prev, [stepId]: updated };
      } else {
        next = { ...prev, [stepId]: option };
      }
      return cascadeSelections(next);
    });
    if (option.image) {
      pendingImageRef.current = getImageUrl(option.image);
    }
  };

  // Also cascade when steps structure changes (editing in real-time)
  useEffect(() => {
    setSelections((prev) => {
      const result = cascadeSelections(prev);
      const prevKeys = Object.keys(prev).sort().join(",");
      const nextKeys = Object.keys(result).sort().join(",");
      if (prevKeys !== nextKeys) return result;
      const changed = Object.keys(result).some((k) => {
        if (Array.isArray(result[k]) && Array.isArray(prev[k])) {
          return (
            result[k].length !== prev[k].length ||
            result[k].some((o, i) => o.id !== prev[k][i]?.id)
          );
        }
        return result[k]?.id !== prev[k]?.id;
      });
      return changed ? result : prev;
    });
  }, [steps, cascadeSelections]);

  const totalPrice = Object.values(selections).reduce((total, sel) => {
    if (!sel) return total;
    if (Array.isArray(sel)) {
      return total + sel.reduce((sum, o) => sum + (o.price || 0), 0);
    }
    if (sel.priceType === "fixed") return sel.price || 0;
    return total + (sel.price || 0);
  }, 0);

  const currentPageData = pages[currentPage];
  const isSummary = currentPage >= pages.length;

  // ─── IMAGE LOGIC ──────────────────────────────────
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const allSelectedImages = useMemo(() => {
    if (!settings.showImages) return [];
    const imgs = [];

    const hasFrame = steps.some((s) => s.type === "frame-color");
    if (hasFrame) {
      let thumbUrl = "";
      const firstFrameStep = steps.find((s) => s.type === "frame-color");
      if (firstFrameStep) {
        if (firstFrameStep.frameImage)
          thumbUrl = getImageUrl(firstFrameStep.frameImage);
        else if (firstFrameStep.options?.length > 0) {
          const opt = firstFrameStep.options.find((o) => o.image);
          if (opt) thumbUrl = getImageUrl(opt.image);
        }
      }
      imgs.push({
        isFrame: true,
        url: thumbUrl || "frame-view",
        stepName: "Basis",
        optionName: "Overzicht",
      });
    }

    steps.forEach((step) => {
      if (
        step.type !== "frame-color" &&
        (step.imageMode || "replace") === "stack"
      )
        return;
      const sel = selections[step.id];
      if (!sel) return;
      const add = (o) => {
        if (o.image)
          imgs.push({
            url: getImageUrl(o.image),
            stepName: step.name,
            optionName: o.name,
            stepId: step.id,
            keepOriginalSize: !!o.keepOriginalSize,
          });
      };
      if (Array.isArray(sel)) sel.forEach(add);
      else add(sel);
    });
    return imgs;
  }, [steps, selections, settings.showImages]);

  // Clamp activeImageIndex when images change
  useEffect(() => {
    if (allSelectedImages.length === 0) {
      setActiveImageIndex(0);
    } else if (activeImageIndex >= allSelectedImages.length) {
      setActiveImageIndex(allSelectedImages.length - 1);
    }
  }, [allSelectedImages.length, activeImageIndex]);

  // Auto-follow: when PAGE changes, show the most relevant image for the new page
  useEffect(() => {
    if (currentPage >= pages.length) {
      const idx = allSelectedImages.findIndex((img) => img.isFrame);
      if (idx >= 0) setActiveImageIndex(idx);
      return;
    }

    const currentStepIds = pages[currentPage]?.steps.map((s) => s.id) || [];

    const firstRelevantImageIndex = allSelectedImages.findIndex(
      (img) =>
        !img.isFrame && img.stepId && currentStepIds.includes(img.stepId),
    );

    if (firstRelevantImageIndex >= 0) {
      setActiveImageIndex(firstRelevantImageIndex);
      return;
    }

    const hasFrameStep = pages[currentPage]?.steps.some(
      (s) => s.type === "frame-color",
    );
    if (hasFrameStep) {
      const idx = allSelectedImages.findIndex((img) => img.isFrame);
      if (idx >= 0) {
        setActiveImageIndex(idx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Follow selection: when user picks an option with an image, jump to it
  useEffect(() => {
    if (!pendingImageRef.current) return;
    const url = pendingImageRef.current;
    pendingImageRef.current = null;
    const idx = allSelectedImages.findIndex((img) => img.url === url);
    if (idx >= 0) setActiveImageIndex(idx);
  }, [allSelectedImages]);

  // One-time: once default selections populate allSelectedImages, jump to
  // the first real (non-frame) image so the viewer doesn't start on white.
  const initialFollowDone = useRef(false);
  useEffect(() => {
    if (initialFollowDone.current) return;
    const firstReal = allSelectedImages.findIndex((img) => !img.isFrame);
    if (firstReal < 0) return;
    initialFollowDone.current = true;
    setActiveImageIndex(firstReal);
  }, [allSelectedImages]);

  const hasAnyImages = useMemo(() => {
    if (!settings.showImages) return false;
    return (
      steps.some((s) => s.options?.some((o) => o.image)) ||
      steps.some(
        (s) => s.type === "frame-color" && (s.svgContent || s.frameImage),
      )
    );
  }, [steps, settings.showImages]);

  // ─── STEP VALIDATION ─────────────────────────────
  const isPageVisible = useCallback(
    (pageIdx) => {
      if (!pages[pageIdx]) return false;
      return pages[pageIdx].steps.some((step) => {
        const isFrameColor = step.type === "frame-color";
        const hasFrameContent =
          isFrameColor && (step.frameImage || step.svgContent);
        if (hasFrameContent) return true;
        return (step.options || []).some((o) =>
          isOptionVisibleWith(o, selections),
        );
      });
    },
    [pages, selections, isOptionVisibleWith],
  );

  const isPageComplete = useCallback(
    (pageIdx) => {
      if (!pages[pageIdx]) return true;
      if (!isPageVisible(pageIdx)) return true;
      return pages[pageIdx].steps.every((step) => {
        if (step.type === "frame-color") return true;
        if (step.required === false) return true;
        const visibleOpts = (step.options || []).filter((o) =>
          isOptionVisibleWith(o, selections),
        );
        if (visibleOpts.length === 0) return true;
        const sel = selections[step.id];
        if (!sel) return false;
        if (step.type === "checkbox")
          return Array.isArray(sel) && sel.length > 0;
        return true;
      });
    },
    [pages, selections, isOptionVisibleWith, isPageVisible],
  );

  const isCurrentPageComplete = isPageComplete(currentPage);

  const maxReachablePage = useMemo(() => {
    for (let i = 0; i < pages.length; i++) {
      if (!isPageComplete(i)) return i;
    }
    return pages.length;
  }, [pages, isPageComplete]);

  // Initialize frame-color steps with default zone colors / frame color
  useEffect(() => {
    setSelections((prev) => {
      let next = { ...prev };
      let changed = false;
      steps.forEach((step) => {
        if (step.type === "frame-color" && !next[step.id]) {
          const optionsWithImages = (step.options || []).filter((o) => o.image);
          if (optionsWithImages.length > 0) {
            const optionColors = {};
            optionsWithImages.forEach((opt) => {
              optionColors[opt.id] = {
                frameColor: opt.colorHex || "#ffffff",
                frameColor2: opt.colorHex2 || "",
                gradientAngle: opt.gradientAngle ?? 90,
              };
            });
            next[step.id] = {
              id: `frame-color-${step.id}`,
              name: "Frame kleuren",
              optionColors,
              price: 0,
              priceType: "included",
            };
            changed = true;
          } else if (step.frameImage) {
            next[step.id] = {
              id: `frame-color-${step.id}`,
              name: "Frame kleuren",
              frameColor: step.frameImageDefaultColor || "#ffffff",
              frameColor2: step.frameImageDefaultColor2 || "",
              gradientAngle: step.frameImageDefaultGradientAngle ?? 90,
              price: 0,
              priceType: "included",
            };
            changed = true;
          } else {
            const zones = (step.zones || []).filter((z) => z.enabled);
            if (zones.length > 0) {
              const zoneColors = {};
              zones.forEach((z) => {
                zoneColors[z.id] = z.defaultColor;
              });
              next[step.id] = {
                id: `frame-color-${step.id}`,
                name: "Frame kleuren",
                zoneColors,
                price: 0,
                priceType: "included",
              };
              changed = true;
            }
          }
        }
      });
      return changed ? next : prev;
    });
  }, [steps]);

  // Map each step to its page index
  const stepPageIndex = useMemo(() => {
    const map = {};
    pages.forEach((page, pageIdx) => {
      page.steps.forEach((step) => {
        map[step.id] = pageIdx;
      });
    });
    return map;
  }, [pages]);

  // Generate colored SVG from any frame-color step
  const frameColorSvg = useMemo(() => {
    for (const step of steps) {
      if (step.type === "frame-color" && step.svgContent) {
        const pageIdx = stepPageIndex[step.id];
        if (pageIdx !== undefined && pageIdx > currentPage) continue;
        const zones = (step.zones || []).filter((z) => z.enabled);
        const sel = selections[step.id];
        const zoneColors = sel?.zoneColors || {};
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(step.svgContent, "image/svg+xml");
          const svgEl = doc.querySelector("svg");
          if (svgEl) {
            if (!svgEl.getAttribute("viewBox")) {
              const w =
                svgEl.getAttribute("width") || svgEl.getBBox?.()?.width || 800;
              const h =
                svgEl.getAttribute("height") ||
                svgEl.getBBox?.()?.height ||
                600;
              svgEl.setAttribute(
                "viewBox",
                `0 0 ${parseFloat(w) || 800} ${parseFloat(h) || 600}`,
              );
            }
            svgEl.removeAttribute("width");
            svgEl.removeAttribute("height");
            svgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
            svgEl.style.width = "100%";
            svgEl.style.height = "100%";
          }
          zones.forEach((zone) => {
            const color = zoneColors[zone.id] || zone.defaultColor;
            const element = doc.getElementById(zone.elementId);
            if (element) {
              element.setAttribute("fill", color);
              element
                .querySelectorAll("[fill]")
                .forEach((child) => child.setAttribute("fill", color));
            }
          });
          return new XMLSerializer().serializeToString(doc);
        } catch {
          return step.svgContent;
        }
      }
    }
    return null;
  }, [selections, steps, stepPageIndex, currentPage]);

  // Get ALL frame-color images (PNG mode)
  const frameColorImages = useMemo(() => {
    const images = [];
    steps.forEach((step, idx) => {
      if (step.type !== "frame-color") return;
      const pageIdx = stepPageIndex[step.id];
      if (pageIdx !== undefined && pageIdx > currentPage) return;
      const sel = selections[step.id];

      const optionsWithImages = (step.options || []).filter(
        (o) => o.image && isOptionVisibleWith(o, selections),
      );
      if (optionsWithImages.length > 0) {
        const optionColors = sel?.optionColors || {};
        optionsWithImages.forEach((opt) => {
          const oc = optionColors[opt.id] || {};
          images.push({
            url: getImageUrl(opt.image),
            color: oc.frameColor || opt.colorHex || "#ffffff",
            color2: oc.frameColor2 || opt.colorHex2 || "",
            gradientAngle: oc.gradientAngle ?? opt.gradientAngle ?? 90,
            zIndex: idx,
          });
        });
        return;
      }

      if (step.frameImage) {
        images.push({
          url: getImageUrl(step.frameImage),
          color: sel?.frameColor || step.frameImageDefaultColor || "#ffffff",
          color2: sel?.frameColor2 || "",
          gradientAngle: sel?.gradientAngle ?? 90,
          zIndex: idx,
        });
      }
    });
    return images;
  }, [selections, steps, stepPageIndex, currentPage, isOptionVisibleWith]);

  // Collect stacked overlay images
  const stackedOverlays = useMemo(() => {
    const overlays = [];
    steps.forEach((step, idx) => {
      if (step.type === "frame-color") return;
      if ((step.imageMode || "replace") !== "stack") return;
      const sel = selections[step.id];
      if (!sel) return;
      const options = Array.isArray(sel) ? sel : [sel];
      options.forEach((o) => {
        if (o.image) {
          overlays.push({
            url: getImageUrl(o.image),
            zIndex: idx,
            keepOriginalSize: !!o.keepOriginalSize,
          });
        }
      });
    });
    return overlays;
  }, [steps, selections]);

  // ─── THEME ──────────────────────────────────────────
  const accentColor = settings.accentColor || "#1E4FCF";
  const template = settings.layoutTemplate || "dark";

  const theme = useMemo(() => {
    const isDark = template === "dark";
    const isMinimal = template === "minimal";
    return {
      isDark,
      isMinimal,
      root: isDark
        ? "bg-black text-white"
        : isMinimal
          ? "bg-gray-50 text-gray-900"
          : "bg-white text-gray-900",
      rootBorder: isDark ? "border-white/10" : "border-gray-200",
      header: isDark
        ? "bg-black/80 border-white/10"
        : isMinimal
          ? "bg-gray-50/80 border-gray-100"
          : "bg-white/80 border-gray-200",
      headerText: isDark ? "text-white/90" : "text-gray-900",
      headerMuted: isDark ? "text-neutral-400" : "text-gray-400",
      headerPrice: isDark ? "text-neutral-500" : "text-gray-400",
      imageGradient: isDark
        ? "from-white/[0.02] via-transparent to-white/[0.01]"
        : isMinimal
          ? "from-gray-100/50 via-transparent to-gray-50/30"
          : "from-gray-50 via-gray-50/50 to-gray-100/30",
      imageBorder: isDark ? "border-white/5" : "border-gray-100",
      imageEmpty: isDark ? "text-neutral-600" : "text-gray-300",
      arrowBg: isDark
        ? "bg-black/60 border-white/10 text-white/70 hover:text-white hover:bg-black/80"
        : "bg-white/80 border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-white",
      thumbBorderInactive: isDark
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.08)",
      panel: isDark
        ? "bg-neutral-950/80 border-white/10"
        : isMinimal
          ? "bg-white border-gray-100"
          : "bg-white border-gray-200",
      panelBorder: isDark ? "border-white/10" : "border-gray-200",
      panelBorderLight: isDark ? "border-white/5" : "border-gray-100",
      tabActive: "text-white",
      tabInactive: isDark
        ? "text-neutral-500 hover:text-neutral-300"
        : "text-gray-400 hover:text-gray-600",
      heading: isDark ? "text-white" : "text-gray-900",
      muted: isDark ? "text-neutral-500" : "text-gray-400",
      mutedLight: isDark ? "text-neutral-600" : "text-gray-300",
      optionBorder: isDark ? "border-white/10" : "border-gray-200",
      optionBorderHover: isDark
        ? "hover:border-white/20"
        : "hover:border-gray-300",
      optionSelectedBg: isDark ? "bg-white/[0.04]" : "bg-gray-50",
      optionText: isDark ? "text-white" : "text-gray-900",
      optionDesc: isDark ? "text-neutral-500" : "text-gray-400",
      colorRingBg: isDark ? "#000" : "#fff",
      colorRingInactive: isDark
        ? "0 0 0 1px rgba(255,255,255,0.15)"
        : "0 0 0 1px rgba(0,0,0,0.08)",
      checkboxInactive: isDark ? "border-neutral-600" : "border-gray-300",
      navText: isDark
        ? "text-neutral-500 hover:text-white"
        : "text-gray-400 hover:text-gray-700",
      summaryDivide: isDark ? "divide-white/5" : "divide-gray-100",
      summaryBorder: isDark ? "border-white/20" : "border-gray-900",
      priceMuted: isDark ? "text-neutral-400" : "text-gray-500",
    };
  }, [template]);

  // ─── RENDER STACKED OVERLAY ──────────────────────────
  const renderStackedOverlay = (img, i, extraClass = "") => (
    <img
      key={`stack-${i}`}
      src={img.url}
      alt=""
      className={`${
        img.keepOriginalSize
          ? "pointer-events-none"
          : `${extraClass || "absolute inset-0 w-full h-full"} object-contain pointer-events-none`
      }`}
      style={{
        zIndex: 50 + img.zIndex,
        animation: "configuratorFadeIn 0.4s ease-out",
        ...(img.keepOriginalSize
          ? {
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }
          : {}),
      }}
    />
  );

  // ─── RENDER STEP OPTIONS ──────────────────────────────

  const renderStepOptions = (stepData) => {
    const visibleOptions = (stepData.options || []).filter((opt) =>
      isOptionVisible(opt, stepData.id),
    );

    // ── Frame Color type ──
    if (stepData.type === "frame-color") {
      const currentSelection = selections[stepData.id];

      // ── Options mode (new) — each option is a colourable frame layer ──
      const optionsWithImages = visibleOptions.filter((o) => o.image);
      if (optionsWithImages.length > 0) {
        const optionColors = currentSelection?.optionColors || {};

        const updateOptionColor = (optionId, updates) => {
          setSelections((prev) => {
            const prevSel = prev[stepData.id] || {};
            const prevOptColors = prevSel.optionColors || {};
            const prevOC = prevOptColors[optionId] || {};
            return {
              ...prev,
              [stepData.id]: {
                ...prevSel,
                id: `frame-color-${stepData.id}`,
                name: "Frame kleuren",
                optionColors: {
                  ...prevOptColors,
                  [optionId]: {
                    frameColor:
                      updates.frameColor ?? prevOC.frameColor ?? "#ffffff",
                    frameColor2:
                      updates.frameColor2 ?? prevOC.frameColor2 ?? "",
                    gradientAngle:
                      updates.gradientAngle ?? prevOC.gradientAngle ?? 90,
                  },
                },
                price: 0,
                priceType: "included",
              },
            };
          });
        };

        return (
          <div className="space-y-6">
            {optionsWithImages.map((opt) => {
              const oc = optionColors[opt.id] || {};
              const color = oc.frameColor || opt.colorHex || "#ffffff";
              const color2 = oc.frameColor2 || opt.colorHex2 || "";
              const angle = oc.gradientAngle ?? opt.gradientAngle ?? 90;
              const isGradientActive = !!color2;

              return (
                <div key={opt.id} className="space-y-3">
                  {/* Option header */}
                  <div className="flex items-center gap-2">
                    <div
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <h4 className={`font-medium text-sm ${theme.optionText}`}>
                      {opt.name}
                    </h4>
                  </div>

                  {/* Inline tinted image preview */}
                  <div
                    className={`rounded-xl p-3 ${theme.isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
                  >
                    <div className="w-full flex items-center justify-center overflow-hidden rounded-lg bg-white shadow-sm">
                      <TintedImage
                        src={getImageUrl(opt.image)}
                        color={color}
                        color2={color2 || undefined}
                        gradientAngle={angle}
                        alt={opt.name}
                        className="max-w-full max-h-40 object-contain"
                      />
                    </div>
                  </div>

                  {/* Primary color picker */}
                  <div
                    className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${theme.optionBorder}`}
                  >
                    <input
                      type="color"
                      value={color}
                      onChange={(e) =>
                        updateOptionColor(opt.id, {
                          frameColor: e.target.value,
                        })
                      }
                      className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-lg border-0 p-0 appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-none"
                      style={{
                        boxShadow: `0 0 0 2px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className={`font-medium text-sm ${theme.optionText}`}>
                        {isGradientActive ? "Kleur 1" : "Kleur"}
                      </h4>
                      <p
                        className={`text-[10px] font-mono uppercase ${theme.optionDesc}`}
                      >
                        {color}
                      </p>
                    </div>
                  </div>

                  {/* Gradient toggle */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isGradientActive) {
                        updateOptionColor(opt.id, { frameColor2: "" });
                      } else {
                        updateOptionColor(opt.id, { frameColor2: color });
                      }
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
                      isGradientActive
                        ? theme.isDark
                          ? "border-white/20 bg-white/[0.06] text-white"
                          : "border-gray-300 bg-gray-100 text-gray-900"
                        : theme.isDark
                          ? "border-white/[0.06] text-gray-400 hover:text-white hover:border-white/10"
                          : "border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3.5 w-3.5"
                    >
                      <rect x="2" y="2" width="20" height="20" rx="2" />
                      <path d="M2 12h20" />
                      <path d="M12 2v20" />
                    </svg>
                    {isGradientActive ? "Gradient uit" : "Gradient"}
                  </button>

                  {/* Gradient controls */}
                  {isGradientActive && (
                    <div className="space-y-3">
                      <div
                        className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${theme.optionBorder}`}
                      >
                        <input
                          type="color"
                          value={color2}
                          onChange={(e) =>
                            updateOptionColor(opt.id, {
                              frameColor2: e.target.value,
                            })
                          }
                          className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-lg border-0 p-0 appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-none"
                          style={{
                            boxShadow: `0 0 0 2px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <h4
                            className={`font-medium text-sm ${theme.optionText}`}
                          >
                            Kleur 2
                          </h4>
                          <p
                            className={`text-[10px] font-mono uppercase ${theme.optionDesc}`}
                          >
                            {color2}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-xs font-medium ${theme.optionText}`}
                          >
                            Hoek
                          </span>
                          <span
                            className={`text-[10px] font-mono ${theme.optionDesc}`}
                          >
                            {angle}°
                          </span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="360"
                          step="15"
                          value={angle}
                          onChange={(e) =>
                            updateOptionColor(opt.id, {
                              gradientAngle: parseInt(e.target.value),
                            })
                          }
                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(90deg, ${color}, ${color2})`,
                          }}
                        />
                        <div className="flex gap-1.5 justify-center pt-1">
                          {[0, 45, 90, 135, 180].map((a) => (
                            <button
                              key={a}
                              type="button"
                              onClick={() =>
                                updateOptionColor(opt.id, {
                                  gradientAngle: a,
                                })
                              }
                              className={`h-7 w-7 rounded-md text-[10px] font-medium transition-all ${
                                angle === a
                                  ? theme.isDark
                                    ? "bg-white/10 text-white"
                                    : "bg-gray-200 text-gray-900"
                                  : theme.isDark
                                    ? "text-gray-500 hover:text-white hover:bg-white/[0.04]"
                                    : "text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                              }`}
                            >
                              {a}°
                            </button>
                          ))}
                        </div>
                      </div>
                      <div
                        className="h-6 w-full rounded-lg"
                        style={{
                          background: `linear-gradient(${angle}deg, ${color}, ${color2})`,
                          boxShadow: `0 0 0 1px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      // ── PNG/Image mode (old — backwards compat) ──
      if (stepData.frameImage) {
        const currentColor =
          currentSelection?.frameColor ||
          stepData.frameImageDefaultColor ||
          "#ffffff";
        const currentColor2 = currentSelection?.frameColor2 || "";
        const currentGradientAngle = currentSelection?.gradientAngle ?? 90;
        const isGradientActive = !!currentColor2;

        const updateFrameSelection = (updates) => {
          setSelections((prev) => ({
            ...prev,
            [stepData.id]: {
              id: `frame-color-${stepData.id}`,
              name: "Frame kleuren",
              frameColor:
                updates.frameColor ??
                prev[stepData.id]?.frameColor ??
                currentColor,
              frameColor2:
                updates.frameColor2 ?? prev[stepData.id]?.frameColor2 ?? "",
              gradientAngle:
                updates.gradientAngle ?? prev[stepData.id]?.gradientAngle ?? 90,
              price: 0,
              priceType: "included",
            },
          }));
        };

        return (
          <div className="space-y-4">
            {/* Inline tinted image preview */}
            <div
              className={`rounded-xl p-4 ${theme.isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <div className="w-full flex items-center justify-center overflow-hidden rounded-lg bg-white shadow-md">
                <TintedImage
                  src={getImageUrl(stepData.frameImage)}
                  color={currentColor}
                  color2={currentColor2 || undefined}
                  gradientAngle={currentGradientAngle}
                  alt="Frame"
                  className="max-w-full max-h-52 object-contain"
                />
              </div>
            </div>
            {/* Primary color picker */}
            <div
              className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${theme.isDark ? "border-white/10" : "border-gray-200"}`}
            >
              <input
                type="color"
                value={currentColor}
                onChange={(e) =>
                  updateFrameSelection({ frameColor: e.target.value })
                }
                className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-lg border-0 p-0 appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-none"
                style={{
                  boxShadow: `0 0 0 2px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                }}
              />
              <div className="flex-1 min-w-0">
                <h4
                  className={`font-medium text-sm ${theme.isDark ? "text-white" : "text-gray-900"}`}
                >
                  {isGradientActive ? "Kleur 1" : "Frame Kleur"}
                </h4>
                <p
                  className={`text-[10px] font-mono uppercase ${theme.isDark ? "text-neutral-500" : "text-gray-400"}`}
                >
                  {currentColor}
                </p>
              </div>
            </div>

            {/* Gradient toggle */}
            <button
              type="button"
              onClick={() => {
                if (isGradientActive) {
                  updateFrameSelection({ frameColor2: "" });
                } else {
                  updateFrameSelection({ frameColor2: currentColor });
                }
              }}
              className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-xs font-medium transition-all ${
                isGradientActive
                  ? theme.isDark
                    ? "border-white/20 bg-white/[0.06] text-white"
                    : "border-gray-300 bg-gray-100 text-gray-900"
                  : theme.isDark
                    ? "border-white/[0.06] text-gray-400 hover:text-white hover:border-white/10"
                    : "border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {isGradientActive ? "Gradient uit" : "Gradient"}
            </button>

            {/* Gradient controls */}
            {isGradientActive && (
              <div className="space-y-3">
                <div
                  className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${theme.isDark ? "border-white/10" : "border-gray-200"}`}
                >
                  <input
                    type="color"
                    value={currentColor2}
                    onChange={(e) =>
                      updateFrameSelection({ frameColor2: e.target.value })
                    }
                    className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-lg border-0 p-0 appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-none"
                    style={{
                      boxShadow: `0 0 0 2px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4
                      className={`font-medium text-sm ${theme.isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Kleur 2
                    </h4>
                    <p
                      className={`text-[10px] font-mono uppercase ${theme.isDark ? "text-neutral-500" : "text-gray-400"}`}
                    >
                      {currentColor2}
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-medium ${theme.isDark ? "text-white" : "text-gray-900"}`}
                    >
                      Hoek
                    </span>
                    <span
                      className={`text-[10px] font-mono ${theme.isDark ? "text-neutral-500" : "text-gray-400"}`}
                    >
                      {currentGradientAngle}°
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    step="15"
                    value={currentGradientAngle}
                    onChange={(e) =>
                      updateFrameSelection({
                        gradientAngle: parseInt(e.target.value),
                      })
                    }
                    className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(90deg, ${currentColor}, ${currentColor2})`,
                    }}
                  />
                </div>

                <div
                  className="h-6 w-full rounded-lg"
                  style={{
                    background: `linear-gradient(${currentGradientAngle}deg, ${currentColor}, ${currentColor2})`,
                    boxShadow: `0 0 0 1px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                  }}
                />
              </div>
            )}
          </div>
        );
      }

      // ── SVG zone mode ──
      const zones = (stepData.zones || []).filter((z) => z.enabled);
      const zoneColors = currentSelection?.zoneColors || {};

      const getColoredSvg = () => {
        if (!stepData.svgContent) return "";
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(
            stepData.svgContent,
            "image/svg+xml",
          );
          const svgEl = doc.querySelector("svg");
          if (svgEl) {
            if (!svgEl.getAttribute("viewBox")) {
              const w = svgEl.getAttribute("width") || 800;
              const h = svgEl.getAttribute("height") || 600;
              svgEl.setAttribute(
                "viewBox",
                `0 0 ${parseFloat(w) || 800} ${parseFloat(h) || 600}`,
              );
            }
            svgEl.removeAttribute("width");
            svgEl.removeAttribute("height");
            svgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");
            svgEl.style.width = "100%";
            svgEl.style.height = "auto";
            svgEl.style.maxHeight = "13rem";
          }
          zones.forEach((zone) => {
            const color = zoneColors[zone.id] || zone.defaultColor;
            const element = doc.getElementById(zone.elementId);
            if (element) {
              element.setAttribute("fill", color);
              element
                .querySelectorAll("[fill]")
                .forEach((child) => child.setAttribute("fill", color));
            }
          });
          return new XMLSerializer().serializeToString(doc);
        } catch {
          return stepData.svgContent;
        }
      };

      const handleZoneColorChange = (zoneId, color) => {
        const newZoneColors = { ...zoneColors, [zoneId]: color };
        setSelections((prev) => ({
          ...prev,
          [stepData.id]: {
            id: `frame-color-${stepData.id}`,
            name: "Frame kleuren",
            zoneColors: newZoneColors,
            price: 0,
            priceType: "included",
          },
        }));
      };

      return (
        <div className="space-y-4">
          {stepData.svgContent && (
            <div
              className={`rounded-xl p-4 ${theme.isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-gray-50 border border-gray-100"}`}
            >
              <div
                className="w-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-52 [&>svg]:w-auto [&>svg]:h-auto [&>svg]:block [&>svg]:mx-auto"
                dangerouslySetInnerHTML={{ __html: getColoredSvg() }}
              />
            </div>
          )}
          <div className="space-y-2">
            {zones.map((zone) => (
              <div
                key={zone.id}
                className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${theme.isDark ? "border-white/10" : "border-gray-200"}`}
              >
                <input
                  type="color"
                  value={zoneColors[zone.id] || zone.defaultColor}
                  onChange={(e) =>
                    handleZoneColorChange(zone.id, e.target.value)
                  }
                  className="h-10 w-10 flex-shrink-0 cursor-pointer rounded-lg border-0 p-0 appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:rounded-lg [&::-moz-color-swatch]:border-none"
                  style={{
                    boxShadow: `0 0 0 2px ${theme.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)"}`,
                  }}
                />
                <div className="flex-1 min-w-0">
                  <h4
                    className={`font-medium text-sm ${theme.isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {zone.name}
                  </h4>
                  <p
                    className={`text-[10px] font-mono uppercase ${theme.isDark ? "text-neutral-500" : "text-gray-400"}`}
                  >
                    {zoneColors[zone.id] || zone.defaultColor}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // ── Color type ──
    if (stepData.type === "color") {
      return (
        <div className="flex flex-wrap gap-3">
          {visibleOptions.map((option) => {
            const isSelected = selections[stepData.id]?.id === option.id;
            return (
              <button
                key={option.id}
                onClick={() =>
                  option.inStock && handleSelect(stepData.id, option)
                }
                disabled={!option.inStock}
                className={`group flex flex-col items-center gap-1.5 ${
                  !option.inStock
                    ? "opacity-30 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
                title={option.name}
              >
                <div
                  className="h-10 w-10 rounded-full transition-all duration-200"
                  style={{
                    background: option.colorHex2
                      ? `linear-gradient(${option.gradientAngle ?? 90}deg, ${option.colorHex || "#ccc"}, ${option.colorHex2})`
                      : option.colorHex || "#ccc",
                    boxShadow: isSelected
                      ? `0 0 0 2px ${theme.colorRingBg}, 0 0 0 4px ${accentColor}`
                      : theme.colorRingInactive,
                  }}
                >
                  {isSelected && (
                    <div className="h-full w-full rounded-full flex items-center justify-center">
                      <Check className="h-4 w-4 text-white drop-shadow-sm" />
                    </div>
                  )}
                </div>
                <span
                  className={`text-[10px] font-medium truncate max-w-[60px] ${theme.muted}`}
                >
                  {option.name}
                </span>
              </button>
            );
          })}
        </div>
      );
    }

    // ── Visual type ──
    if (stepData.type === "visual") {
      const colorOptions = visibleOptions.filter((o) => o.colorHex);
      const cardOptions = visibleOptions.filter((o) => !o.colorHex);

      return (
        <div className="space-y-4">
          {colorOptions.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {colorOptions.map((option) => {
                const isSelected = selections[stepData.id]?.id === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() =>
                      option.inStock && handleSelect(stepData.id, option)
                    }
                    disabled={!option.inStock}
                    className={`group flex flex-col items-center gap-1.5 ${
                      !option.inStock
                        ? "opacity-30 cursor-not-allowed"
                        : "cursor-pointer"
                    }`}
                    title={option.name}
                  >
                    <div
                      className="h-10 w-10 rounded-full transition-all duration-200"
                      style={{
                        background: option.colorHex2
                          ? `linear-gradient(${option.gradientAngle ?? 90}deg, ${option.colorHex}, ${option.colorHex2})`
                          : option.colorHex,
                        boxShadow: isSelected
                          ? `0 0 0 2px ${theme.colorRingBg}, 0 0 0 4px ${accentColor}`
                          : theme.colorRingInactive,
                      }}
                    >
                      {isSelected && (
                        <div className="h-full w-full rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white drop-shadow-sm" />
                        </div>
                      )}
                    </div>
                    <span
                      className={`text-[10px] font-medium truncate max-w-[60px] ${theme.muted}`}
                    >
                      {option.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {cardOptions.length > 0 && (
            <div
              className={
                settings.optionsLayout === "grid"
                  ? "grid grid-cols-2 gap-2"
                  : "space-y-2"
              }
            >
              {cardOptions.map((option) => {
                const isSelected = selections[stepData.id]?.id === option.id;
                return (
                  <button
                    key={option.id}
                    onClick={() =>
                      option.inStock && handleSelect(stepData.id, option)
                    }
                    disabled={!option.inStock}
                    className={`w-full flex items-center gap-3 rounded-lg border p-2.5 transition-all duration-200 text-left ${
                      isSelected
                        ? ""
                        : `${theme.optionBorder} ${theme.optionBorderHover}`
                    } ${!option.inStock ? "opacity-30 cursor-not-allowed" : ""}`}
                    style={
                      isSelected
                        ? {
                            borderColor: `${accentColor}80`,
                            backgroundColor: `${accentColor}10`,
                          }
                        : {}
                    }
                  >
                    {option.image && settings.showImages && (
                      <img
                        src={getImageUrl(option.image)}
                        alt={option.name}
                        className="h-12 w-12 rounded-lg object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4
                          className={`font-medium text-sm truncate ${theme.optionText}`}
                        >
                          {option.name}
                        </h4>
                        {isSelected && (
                          <div
                            className="h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: accentColor }}
                          >
                            <Check className="h-2.5 w-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      {option.description && (
                        <p className={`text-xs truncate ${theme.optionDesc}`}>
                          {option.description}
                        </p>
                      )}
                    </div>
                    {option.price !== 0 && settings.showPrices && (
                      <span
                        className="text-xs font-semibold flex-shrink-0"
                        style={{ color: accentColor }}
                      >
                        {option.priceType === "addition"
                          ? option.price > 0
                            ? "+"
                            : ""
                          : ""}
                        €{Number(option.price).toFixed(2)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // ── Checkbox type ──
    if (stepData.type === "checkbox") {
      const checkedOptions = Array.isArray(selections[stepData.id])
        ? selections[stepData.id]
        : [];
      return (
        <div className="space-y-2">
          {visibleOptions.map((option) => {
            const isChecked = checkedOptions.some((o) => o.id === option.id);
            return (
              <button
                key={option.id}
                onClick={() =>
                  option.inStock && handleSelect(stepData.id, option)
                }
                disabled={!option.inStock}
                className={`w-full flex items-center gap-3 rounded-lg border p-2.5 transition-all duration-200 text-left ${
                  isChecked
                    ? ""
                    : `${theme.optionBorder} ${theme.optionBorderHover}`
                } ${!option.inStock ? "opacity-30 cursor-not-allowed" : ""}`}
                style={
                  isChecked
                    ? {
                        borderColor: `${accentColor}60`,
                        backgroundColor: `${accentColor}08`,
                      }
                    : {}
                }
              >
                <div className="flex-shrink-0">
                  {isChecked ? (
                    <div
                      className="h-5 w-5 rounded flex items-center justify-center"
                      style={{ backgroundColor: accentColor }}
                    >
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                  ) : (
                    <div
                      className={`h-5 w-5 rounded border-2 ${theme.checkboxInactive}`}
                    />
                  )}
                </div>
                {option.image && settings.showImages && (
                  <img
                    src={getImageUrl(option.image)}
                    alt={option.name}
                    className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-medium text-sm ${theme.optionText}`}>
                    {option.name}
                  </h4>
                  {option.description && (
                    <p
                      className={`text-xs mt-0.5 truncate ${theme.optionDesc}`}
                    >
                      {option.description}
                    </p>
                  )}
                </div>
                {option.price !== 0 && settings.showPrices && (
                  <span
                    className="text-xs font-semibold flex-shrink-0"
                    style={{ color: accentColor }}
                  >
                    {option.price > 0 ? "+" : ""}€
                    {Number(option.price).toFixed(2)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      );
    }

    // ── Default: select / size ──
    const useGrid = settings.optionsLayout === "grid";
    return (
      <div className={useGrid ? "grid grid-cols-2 gap-2" : "space-y-2"}>
        {visibleOptions.map((option) => {
          const isSelected = selections[stepData.id]?.id === option.id;
          return (
            <button
              key={option.id}
              onClick={() =>
                option.inStock && handleSelect(stepData.id, option)
              }
              disabled={!option.inStock}
              className={`w-full flex items-center gap-3 rounded-lg border p-2.5 transition-all duration-200 text-left ${
                isSelected
                  ? ""
                  : `${theme.optionBorder} ${theme.optionBorderHover}`
              } ${!option.inStock ? "opacity-30 cursor-not-allowed" : ""}`}
              style={
                isSelected
                  ? {
                      borderColor: `${accentColor}60`,
                      backgroundColor: `${accentColor}08`,
                    }
                  : {}
              }
            >
              {option.colorHex && settings.showImages && (
                <div
                  className="h-8 w-8 rounded-full flex-shrink-0"
                  style={{
                    background: option.colorHex2
                      ? `linear-gradient(${option.gradientAngle ?? 90}deg, ${option.colorHex}, ${option.colorHex2})`
                      : option.colorHex,
                    boxShadow: isSelected
                      ? `0 0 0 2px ${theme.colorRingBg}, 0 0 0 3px ${accentColor}`
                      : theme.colorRingInactive,
                  }}
                />
              )}
              {option.image && !option.colorHex && settings.showImages && (
                <img
                  src={getImageUrl(option.image)}
                  alt={option.name}
                  className="h-8 w-8 rounded-lg object-cover flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <h4
                  className={`font-medium text-sm truncate ${theme.optionText}`}
                >
                  {option.name}
                </h4>
                {option.description && (
                  <p className={`text-xs truncate ${theme.optionDesc}`}>
                    {option.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {option.price !== 0 && settings.showPrices && (
                  <span
                    className="text-xs font-semibold"
                    style={{ color: accentColor }}
                  >
                    {option.priceType === "addition"
                      ? option.price > 0
                        ? "+"
                        : ""
                      : ""}
                    €{Number(option.price).toFixed(2)}
                  </span>
                )}
                {isSelected && (
                  <div
                    className="h-4 w-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Check className="h-2.5 w-2.5 text-white" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // ─── MAIN RENDER ──────────────────────────────────────
  const isLeftPanel = settings.panelPosition === "left";

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${theme.root}`}>
      {/* CSS animations */}
      <style>{`
        @keyframes configuratorFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes configuratorSlideIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ── PREVIEW BANNER ── */}
      <div className="flex-shrink-0 bg-amber-500 text-black text-center py-1.5 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 relative">
        <Eye className="h-3.5 w-3.5" />
        Preview Modus — Dit is een voorbeeld
        <button
          onClick={onClose}
          className="absolute right-3 p-1 rounded hover:bg-amber-600/30 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── TOP BAR ── */}
      <header
        className={`flex-shrink-0 h-14 border-b backdrop-blur-xl flex items-center justify-between px-4 md:px-6 ${theme.header}`}
      >
        <button
          onClick={onClose}
          className={`flex items-center gap-2 text-xs font-medium uppercase tracking-wider cursor-pointer hover:opacity-80 transition-colors ${theme.headerMuted}`}
        >
          <X className="h-4 w-4" />
          <span className="hidden sm:inline">Sluiten</span>
        </button>

        <h2
          className={`text-xs sm:text-sm font-bold uppercase tracking-[0.2em] absolute left-1/2 -translate-x-1/2 pointer-events-none ${theme.headerText}`}
        >
          {name}
        </h2>

        {settings.showPrices !== false && (
          <div className="flex items-center gap-2">
            <span
              className={`hidden sm:inline text-[10px] uppercase tracking-widest font-semibold ${theme.headerPrice}`}
            >
              Totaal
            </span>
            <span
              className="text-base sm:text-lg font-bold tabular-nums"
              style={{ color: accentColor }}
            >
              €{totalPrice.toFixed(2)}
            </span>
          </div>
        )}
      </header>

      {/* ── MAIN CONTENT ── */}
      <div
        className={`flex-1 flex flex-col lg:flex-row overflow-hidden ${isLeftPanel ? "lg:flex-row-reverse" : ""}`}
      >
        {/* ── IMAGE VIEWER ── */}
        {hasAnyImages && (
          <div
            className={`h-[35vh] lg:h-auto lg:flex-1 flex flex-col border-b lg:border-b-0 relative ${theme.imageBorder}`}
          >
            {/* Main image display */}
            <div className="flex-1 relative flex items-center justify-center p-4 lg:p-8 overflow-hidden">
              <div
                className={`absolute inset-0 bg-gradient-to-br ${theme.imageGradient}`}
              />

              {allSelectedImages.length > 0 &&
              allSelectedImages[activeImageIndex] ? (
                allSelectedImages[activeImageIndex].isFrame ? (
                  frameColorSvg ? (
                    <div
                      key="frame-svg"
                      className="relative w-full h-full flex items-center justify-center"
                      style={{ animation: "configuratorFadeIn 0.4s ease-out" }}
                    >
                      <div
                        className="w-full h-full"
                        dangerouslySetInnerHTML={{ __html: frameColorSvg }}
                      />
                      {stackedOverlays.map((img, i) =>
                        renderStackedOverlay(img, i),
                      )}
                    </div>
                  ) : (
                    <div
                      key="frame-imgs"
                      className="relative w-full h-full flex items-center justify-center"
                      style={{ animation: "configuratorFadeIn 0.4s ease-out" }}
                    >
                      <div className="relative w-full h-full rounded-lg overflow-hidden bg-white">
                        {frameColorImages.map((fImg, fi) => (
                          <TintedImage
                            key={`frame-layer-${fi}`}
                            src={fImg.url}
                            color={fImg.color}
                            color2={fImg.color2 || undefined}
                            gradientAngle={fImg.gradientAngle}
                            alt="Frame"
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              zIndex: fi,
                            }}
                          />
                        ))}
                        {stackedOverlays.map((img, i) =>
                          renderStackedOverlay(img, i),
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <img
                      key={allSelectedImages[activeImageIndex]?.url}
                      src={allSelectedImages[activeImageIndex].url}
                      alt={allSelectedImages[activeImageIndex].optionName || ""}
                      className={`${
                        allSelectedImages[activeImageIndex].keepOriginalSize
                          ? "max-w-full max-h-full object-contain"
                          : `w-full h-full ${settings.imageDisplay === "cover" ? "object-cover" : "object-contain"}`
                      }`}
                      style={{ animation: "configuratorFadeIn 0.4s ease-out" }}
                    />
                    {stackedOverlays.map((img, i) =>
                      renderStackedOverlay(img, i),
                    )}
                  </div>
                )
              ) : stackedOverlays.length > 0 ? (
                <div className="relative w-full h-full flex items-center justify-center">
                  {stackedOverlays.map((img, i) =>
                    renderStackedOverlay(
                      img,
                      i,
                      i === 0
                        ? "w-full h-full"
                        : "absolute inset-0 w-full h-full",
                    ),
                  )}
                </div>
              ) : (
                <div
                  className={`flex flex-col items-center gap-3 ${theme.imageEmpty}`}
                >
                  <ImageIcon className="h-12 w-12" />
                  <p className="text-xs font-medium">
                    Selecteer opties om een preview te zien
                  </p>
                </div>
              )}

              {/* Arrow navigation */}
              {allSelectedImages.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setActiveImageIndex((prev) =>
                        prev <= 0 ? allSelectedImages.length - 1 : prev - 1,
                      )
                    }
                    className={`absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border backdrop-blur-sm flex items-center justify-center transition-all ${theme.arrowBg}`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() =>
                      setActiveImageIndex((prev) =>
                        prev >= allSelectedImages.length - 1 ? 0 : prev + 1,
                      )
                    }
                    className={`absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full border backdrop-blur-sm flex items-center justify-center transition-all ${theme.arrowBg}`}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            {settings.showThumbnails !== false &&
              allSelectedImages.length > 0 && (
                <div
                  className={`flex-shrink-0 border-t px-3 py-2 lg:px-4 lg:py-2.5 ${theme.imageBorder}`}
                >
                  <div className="flex items-center gap-2 overflow-x-auto">
                    {allSelectedImages.map((img, i) => (
                      <button
                        key={`thumb-${i}`}
                        onClick={() => setActiveImageIndex(i)}
                        className={`flex-shrink-0 h-11 w-11 lg:h-13 lg:w-13 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                          activeImageIndex === i
                            ? "opacity-100 scale-105"
                            : "opacity-40 hover:opacity-70"
                        }`}
                        style={{
                          borderColor:
                            activeImageIndex === i
                              ? accentColor
                              : theme.thumbBorderInactive,
                        }}
                        title={`${img.stepName}: ${img.optionName}`}
                      >
                        <img
                          src={img.url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
          </div>
        )}

        {/* ── OPTIONS PANEL ── */}
        <div
          className={`flex-1 lg:flex-none ${hasAnyImages ? "lg:w-[400px] xl:w-[440px]" : "w-full max-w-2xl mx-auto"} flex flex-col ${isLeftPanel ? "border-r-0 lg:border-r" : "border-l-0 lg:border-l"} ${theme.panel}`}
        >
          {isSummary ? (
            /* ═══════════ SUMMARY VIEW ═══════════ */
            <div className="flex flex-col h-full">
              <div
                className={`px-4 pt-5 pb-3 border-b ${theme.panelBorderLight}`}
              >
                <p
                  className={`text-[10px] uppercase tracking-widest font-semibold ${theme.muted}`}
                >
                  Samenvatting
                </p>
                <h3 className={`text-lg font-bold mt-1 ${theme.heading}`}>
                  {name}
                </h3>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-2">
                <div className={`${theme.summaryDivide}`}>
                  {steps
                    .filter((step) => !step.hiddenLayer)
                    .map((step) => {
                      const selected = selections[step.id];
                      const isCheckbox = step.type === "checkbox";
                      const checkboxItems =
                        isCheckbox && Array.isArray(selected) ? selected : [];
                      const singleItem = !isCheckbox ? selected : null;
                      return (
                        <div
                          key={step.id}
                          className="py-3.5 flex items-center justify-between gap-4"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            {singleItem?.colorHex && (
                              <div
                                className="h-8 w-8 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: singleItem.colorHex,
                                  boxShadow: theme.colorRingInactive,
                                }}
                              />
                            )}
                            {singleItem?.image &&
                              !singleItem?.colorHex &&
                              settings.showImages && (
                                <img
                                  src={getImageUrl(singleItem.image)}
                                  alt={singleItem.name}
                                  className="h-8 w-8 rounded-full object-cover flex-shrink-0 bg-white/5"
                                />
                              )}
                            <div className="min-w-0">
                              <p
                                className={`text-[10px] font-medium uppercase tracking-wide ${theme.muted}`}
                              >
                                {step.name}
                              </p>
                              <p
                                className={`font-medium text-sm truncate mt-0.5 ${theme.heading}`}
                              >
                                {isCheckbox ? (
                                  checkboxItems.length > 0 ? (
                                    checkboxItems.map((o) => o.name).join(", ")
                                  ) : (
                                    <span
                                      className={`italic font-normal ${theme.mutedLight}`}
                                    >
                                      Niet gekozen
                                    </span>
                                  )
                                ) : singleItem?.name ? (
                                  singleItem.name
                                ) : (
                                  <span
                                    className={`italic font-normal ${theme.mutedLight}`}
                                  >
                                    Niet gekozen
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          {isCheckbox &&
                            checkboxItems.length > 0 &&
                            settings.showPrices && (
                              <span
                                className="font-medium text-sm flex-shrink-0"
                                style={{ color: accentColor }}
                              >
                                +€
                                {checkboxItems
                                  .reduce((s, o) => s + (o.price || 0), 0)
                                  .toFixed(2)}
                              </span>
                            )}
                          {!isCheckbox &&
                            singleItem?.price !== 0 &&
                            settings.showPrices && (
                              <span
                                className="font-medium text-sm flex-shrink-0"
                                style={{ color: accentColor }}
                              >
                                {singleItem.priceType === "addition"
                                  ? singleItem.price > 0
                                    ? "+"
                                    : ""
                                  : ""}
                                €{Number(singleItem.price).toFixed(2)}
                              </span>
                            )}
                        </div>
                      );
                    })}
                </div>
                {settings.showPrices && (
                  <div
                    className={`mt-4 pt-4 border-t-2 flex items-center justify-between ${theme.summaryBorder}`}
                  >
                    <span className={`text-base font-bold ${theme.heading}`}>
                      Totaal
                    </span>
                    <span
                      className="text-2xl font-bold"
                      style={{ color: accentColor }}
                    >
                      €{totalPrice.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div
                className={`px-4 py-3 border-t flex items-center justify-between ${theme.panelBorder}`}
              >
                <button
                  onClick={() => setCurrentPage(pages.length - 1)}
                  className={`flex items-center gap-1 text-xs transition-colors ${theme.navText}`}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Terug
                </button>
                <button
                  className="px-5 py-2 rounded-full text-xs font-semibold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: accentColor }}
                >
                  Configuratie bevestigen
                </button>
              </div>
            </div>
          ) : (
            /* ═══════════ STEP PANEL ═══════════ */
            <div className="flex flex-col h-full">
              <div
                className={`flex-shrink-0 border-b px-4 pt-3.5 pb-0 ${theme.panelBorder}`}
              >
                <div className="flex gap-1 overflow-x-auto pb-3">
                  {pages.map((page, i) => {
                    const canReach = i <= maxReachablePage;
                    if (!isPageVisible(i)) return null;
                    return (
                      <button
                        key={i}
                        onClick={() => canReach && setCurrentPage(i)}
                        disabled={!canReach}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                          i === currentPage
                            ? theme.tabActive
                            : theme.tabInactive
                        }`}
                        style={
                          i === currentPage
                            ? { backgroundColor: accentColor }
                            : {}
                        }
                      >
                        {page.label}
                      </button>
                    );
                  })}
                  {settings.summaryEnabled && (
                    <button
                      onClick={() =>
                        maxReachablePage >= pages.length &&
                        setCurrentPage(pages.length)
                      }
                      disabled={maxReachablePage < pages.length}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all disabled:opacity-30 disabled:cursor-not-allowed ${theme.tabInactive}`}
                    >
                      Samenvatting
                    </button>
                  )}
                </div>
              </div>

              <div
                key={`header-${currentPage}`}
                className="px-4 pt-5 pb-2"
                style={{ animation: "configuratorSlideIn 0.3s ease-out" }}
              >
                <p
                  className={`text-[10px] uppercase tracking-widest font-semibold ${theme.muted}`}
                >
                  Stap {currentPage + 1} van {pages.length}
                </p>
                <h3
                  className={`text-lg font-bold mt-1 leading-tight ${theme.heading}`}
                >
                  {currentPageData?.steps.length === 1
                    ? currentPageData.steps[0].name
                    : currentPageData?.label}
                </h3>
                {currentPageData?.steps.length === 1 &&
                  currentPageData.steps[0].description && (
                    <p
                      className={`text-xs mt-1.5 leading-relaxed ${theme.muted}`}
                    >
                      {currentPageData.steps[0].description}
                    </p>
                  )}
              </div>

              <div
                key={`options-${currentPage}`}
                className="flex-1 overflow-y-auto px-4 py-3"
                style={{ animation: "configuratorSlideIn 0.35s ease-out" }}
              >
                {currentPageData?.steps.map((stepData, stepIndex) => {
                  const isFrameColor = stepData.type === "frame-color";
                  const hasFrameContent =
                    isFrameColor &&
                    (stepData.frameImage || stepData.svgContent);
                  const hasVisibleOptions = (stepData.options || []).some(
                    (opt) => isOptionVisible(opt),
                  );
                  if (!hasFrameContent && !hasVisibleOptions) return null;

                  return (
                    <div
                      key={stepData.id}
                      className={
                        stepIndex > 0
                          ? `mt-5 pt-5 border-t ${theme.panelBorderLight}`
                          : ""
                      }
                    >
                      {currentPageData.steps.length > 1 && (
                        <h4
                          className={`text-sm font-semibold mb-1.5 ${theme.heading}`}
                        >
                          {stepData.name}
                        </h4>
                      )}
                      {stepData.description &&
                        currentPageData.steps.length > 1 && (
                          <p
                            className={`text-xs mb-4 leading-relaxed ${theme.muted}`}
                          >
                            {stepData.description}
                          </p>
                        )}
                      {renderStepOptions(stepData)}
                    </div>
                  );
                })}
              </div>

              <div
                className={`flex-shrink-0 border-t px-4 py-3 flex items-center justify-between ${theme.panelBorder}`}
              >
                <button
                  onClick={() => {
                    let prev = currentPage - 1;
                    while (prev > 0 && !isPageVisible(prev)) prev--;
                    setCurrentPage(Math.max(0, prev));
                  }}
                  disabled={currentPage === 0}
                  className={`flex items-center gap-1 text-xs disabled:opacity-25 disabled:cursor-not-allowed transition-colors ${theme.navText}`}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Vorige
                </button>
                <button
                  onClick={() => {
                    if (!isCurrentPageComplete) return;
                    let next = currentPage + 1;
                    const max =
                      pages.length + (settings.summaryEnabled ? 0 : -1);
                    while (next < pages.length && !isPageVisible(next)) next++;
                    if (next <= max) setCurrentPage(next);
                  }}
                  disabled={
                    !isCurrentPageComplete ||
                    (currentPage === pages.length - 1 &&
                      settings.summaryEnabled === false)
                  }
                  className="px-4 py-2 rounded-full text-xs font-semibold text-white disabled:opacity-25 disabled:cursor-not-allowed transition-all hover:opacity-90 flex items-center gap-1"
                  style={{ backgroundColor: accentColor }}
                >
                  {currentPage === pages.length - 1 &&
                  settings.summaryEnabled !== false
                    ? "Samenvatting"
                    : "Volgende"}
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfiguratorPreview;
